import { observer } from 'mobx-react-lite'
import React, { useContext, useMemo, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { InfluencerContext } from '../../../contexts/InfluencerContext'
import { SharedCampaignContext } from '../../../contexts/Advertiser/SharedCampaignContext'
import { SharedInfluencerContext } from '../../../contexts/Advertiser/SharedInfluencerContext'
import Authentication from '../../../models/Authentication'
import axiosService from '../../../utils/AxiosService'
import RatingStars from '../../Shared/RatingStars'
import { providerLogo, formatNumber, socialPlatformLink } from '../../../utils/Helper'
import { toastError, toastInfo } from '../../FormComponents/Toasts'
import { format, subMonths, subWeeks, isBefore, isValid } from 'date-fns'
import { BoxArrowUpRight, Calendar, Check, PencilSquare } from 'react-bootstrap-icons'
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import UF from '../../FormComponents/UpdatableField'
import { toastSuccess } from '../../FormComponents/Toasts'
import SelectionStatusPicker from '../../Shared/SelectionStatusPicker'
import SelectionStatusIcon from '../../Shared/SelectionStatusIcon'
import ReachbirdApprovedBadge from '../../Shared/ReachbirdApprovedBadge'
import CreatorAvatar from '../../Shared/CreatorAvatar'

export default observer(function InfluencerHeader() {
  const { id, authentication_id, campaign_authentication_id, uuid } = useParams()
  const influencerContext = useContext(InfluencerContext)
  const sharedCampaignContext = useContext(SharedCampaignContext)
  const sharedInfluencerContext = useContext(SharedInfluencerContext)
  const [authentications, setAuthentications] = useState([])
  const [advertiserInfluencer, setAdvertiserInfluencer] = useState(null)
  const [sharedInfluencer, setSharedInfluencer] = useState(null)

  const isAdvertiserContext = !!sharedCampaignContext?.campaignState
  const isInfluencerShareContext = !!sharedInfluencerContext?.influencerState
  const currentId = isInfluencerShareContext
    ? (sharedInfluencerContext?.authentications?.[0]?.id)
    : (isAdvertiserContext ? authentication_id : id)

  useEffect(() => {
    if (isAdvertiserContext && authentication_id) {
      axiosService.get(`/api/advertiser/campaign_share/authentications/${authentication_id}/authentications`)
        .then(authentications => {
          if (!authentications || !Array.isArray(authentications)) {
            setAuthentications([])
            return
          }
          const mappedAuthentications = authentications
            .filter(authentication => authentication && authentication.id)
            .map(authentication => new Authentication(authentication))
          setAuthentications(mappedAuthentications)
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.error || error?.data?.error || error?.error || error?.message || 'An error occurred'
          toastError(errorMessage)
        })
    }
  }, [isAdvertiserContext, authentication_id])

  useEffect(() => {
    if (isAdvertiserContext && authentication_id) {
      axiosService.get(`/api/advertiser/campaign_share/authentications/${authentication_id}/influencer`)
        .then(influencer => setAdvertiserInfluencer(influencer))
        .catch((error) => {
          const errorMessage = error?.response?.data?.error || error?.data?.error || error?.error || error?.message || 'An error occurred'
          // Silently fail if no influencer found
          if (errorMessage !== 'No Influencer found for this authentication') {
            toastError(errorMessage)
          }
        })
    }
  }, [isAdvertiserContext, authentication_id])

  useEffect(() => {
    if (isInfluencerShareContext && sharedInfluencerContext?.authentication?.influencer_id && uuid) {
      axiosService.get(`/api/advertiser/influencer_share/authentications/${uuid}/influencer`)
        .then(influencer => setSharedInfluencer(influencer))
        .catch((error) => {
          // Silently fail if no influencer found
          console.log('No influencer found for shared authentication')
        })
    }
  }, [isInfluencerShareContext, sharedInfluencerContext?.authentication?.id, uuid])

  const pmAuthentications = influencerContext?.authentications || []
  const sharedInfluencerAuthentications = sharedInfluencerContext?.authentications || []
  const currentAuthentications = isInfluencerShareContext
    ? sharedInfluencerAuthentications
    : (isAdvertiserContext ? authentications : pmAuthentications)
  const influencer = isInfluencerShareContext
    ? sharedInfluencer
    : (isAdvertiserContext ? advertiserInfluencer : influencerContext?.influencer)
  const ratingCount = influencerContext?.ratings?.length || 0
  const reachbirdApproved = (isAdvertiserContext || isInfluencerShareContext) ? !!influencer?.reachbird_approved : !!influencerContext?.reachbirdApproved

  const advertiserCampaignAuth = useMemo(() => {
    if (!isAdvertiserContext || !campaign_authentication_id) return null
    return sharedCampaignContext?.campaignState?.campaignAuthentications?.find(
      a => a.campaign_authentication_id === +campaign_authentication_id
    )
  }, [isAdvertiserContext, campaign_authentication_id, sharedCampaignContext?.campaignState?.campaignAuthentications])

  const currentAuthentication = useMemo(() => {
    if (!currentId || !currentAuthentications) {
      return new Authentication()
    }
    // Try to find the authentication with matching id
    const found = currentAuthentications.find(authentication => {
      if (!authentication?.id) return false
      return String(authentication.id) === String(currentId) || +authentication.id === +currentId
    })
    return found || new Authentication()
  }, [currentId, currentAuthentications, currentAuthentications?.length])
  const displayCountry = useMemo(() => {
    if (currentAuthentication?.country) return currentAuthentication.country
    const withCountry = (currentAuthentications || []).filter(authentication => authentication?.country)
    return withCountry.sort((a, b) => (b.reach || 0) - (a.reach || 0))[0]?.country || ''
  }, [currentAuthentication?.country, currentAuthentications, currentAuthentications?.length])
  const currentAvatar = useMemo(() => {
    if (!currentAuthentication?.avatar) return null
    return typeof currentAuthentication.avatar === 'string' ? currentAuthentication.avatar : currentAuthentication.avatar?.url
  }, [currentAuthentication?.id, currentAuthentication?.avatar])

  const requestInfluencerUpdate = () => {
    if (isAdvertiserContext || !currentAuthentication?.provider) return // Don't allow updates in advertiser context
    axiosService.patch(`/api/project_manager/authentications/${id}/influencer_update`, { provider: currentAuthentication.provider })
    .then(({ message }) => {
      toastInfo(message)
    })
    .catch((error) => {
      const errorMessage = error?.response?.data?.error || error?.data?.error || error?.error || error?.message || 'An error occurred'
      toastError(errorMessage)
    })
  }

  const getProviderColor = provider => ({
    instagram: 'text-pink-600',
    tiktok: '',
    youtube: 'text-red-500',
    linkedin: 'text-rb-linkedin'
  }[provider])

  return (
    <div className="flex p-8 justify-between items-center bg-rb-grey dark:bg-rb-dark-mode">
      <div>
        <div className="flex">
          <CreatorAvatar src={currentAvatar} name={influencer?.full_name || currentAuthentication?.username} className="rounded-2xl h-28 w-28 shrink-0" textClassName="text-3xl" />
          <div className="ml-8">
            <div className="flex items-center">
              {displayCountry && getUnicodeFlagIcon(displayCountry)}
              <p className="ml-1 font-semibold dark:text-white">{displayCountry}</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="font-bold font-xl dark:text-white">{influencer?.full_name || currentAuthentication?.username || 'No Influencer for this Authentication'}</p>
              {currentAuthentication?.modash_updatable && <div className="flex items-center justify-center gap-1 rounded-2xl bg-rb-light-green px-4 py-2 h-6 w-28 dark:bg-rb-green/10">
                <Check className="opacity-100 text-rb-green"/>
                <p className="opacity-100 text-rb-green text-xs">Full Report</p>
              </div>}
              {currentAuthentication?.manually_entered && <div title="Not synced with Modash - this account's stats are entered and kept up to date by hand." className="flex items-center justify-center gap-1 rounded-2xl bg-amber-100 px-4 py-2 h-6 w-28 dark:bg-amber-500/15">
                <PencilSquare className="opacity-100 text-amber-700 dark:text-amber-400 text-xs"/>
                <p className="opacity-100 text-amber-700 dark:text-amber-400 text-xs">Manual</p>
              </div>}
              {reachbirdApproved && <ReachbirdApprovedBadge />}
            </div>
            {!isAdvertiserContext && !isInfluencerShareContext && influencer?.email && (
              <UF
                url={`/api/project_manager/influencers/${influencer.id}`}
                model='influencer'
                attribute='email'
                className='h-7 dark:text-rb-dark-mode-text-grey'
                onFieldUpdate={(updatedInfluencer) => {
                  influencer.email = updatedInfluencer.email
                  toastSuccess("Influencer email has been updated")
                }}
              >
                {influencer.email}
              </UF>
            )}
            {isInfluencerShareContext && influencer?.email && (
              <p className="text-sm text-gray-600 dark:text-rb-dark-mode-text-light">{influencer.email}</p>
            )}
            <div className="flex items-center mt-2 gap-3">
              { currentAuthentications?.filter(authentication => authentication?.id).map(authentication =>
                <button
                  key={authentication.id}
                  className="flex items-center gap-2 border-1 border-widget-border dark:border-rb-dark-mode-lighter rounded-lg py-1 px-2 hover:bg-rb-grey dark:hover:bg-rb-dark-mode-lighter"
                  onClick={() => window.open(socialPlatformLink(authentication.provider, authentication.username), '_blank')}
                >
                  <div className={`${getProviderColor(authentication.provider)}`}>{providerLogo(authentication.provider)}</div>
                  <p className="text-sm text-rb-mat-grey dark:text-gray-400">{formatNumber(authentication.reach)}</p>
                  <p className="text-sm text-rb-mat-grey dark:text-gray-400">{authentication.username}</p>
                  <BoxArrowUpRight className='text-sm text-rb-black dark:text-white' />
                </button>
              )}
              { !isAdvertiserContext && !isInfluencerShareContext && influencer?.youtube_username &&
                <button
                  className="flex items-center gap-2 border-1 border-widget-border dark:border-rb-dark-mode-lighter rounded-lg py-1 px-2 hover:bg-rb-grey dark:hover:bg-rb-dark-mode-lighter"
                  onClick={() => window.open(socialPlatformLink('youtube', influencer.youtube_username), '_blank')}
                >
                  <div className={`${getProviderColor('youtube')}`}>{providerLogo('youtube')}</div>
                  <p className="text-sm text-rb-mat-grey dark:text-gray-400">{influencer.youtube_username}</p>
                  <BoxArrowUpRight className='text-sm text-rb-black dark:text-white' />
                </button>
                || null
              }
              { !isAdvertiserContext && !isInfluencerShareContext && influencer?.linkedin_username &&
                <button
                  className="flex items-center gap-2 border-1 border-widget-border dark:border-rb-dark-mode-lighter rounded-lg py-1 px-2 hover:bg-rb-grey dark:hover:bg-rb-dark-mode-lighter"
                  onClick={() => window.open(socialPlatformLink('linkedin', influencer.linkedin_username), '_blank')}
                >
                  <div className={`${getProviderColor('linkedin')}`}>{providerLogo('linkedin')}</div>
                  <p className="text-sm text-rb-mat-grey dark:text-gray-400">{influencer.linkedin_username}</p>
                  <BoxArrowUpRight className='text-sm text-rb-black dark:text-white' />
                </button>
                || null
              }
            </div>
          </div>
        </div>
      </div>
      <div className='flex flex-col'>
        <div className='flex items-center gap-2'>
          <p className='bg-rb-grey dark:bg-rb-dark-mode-lighter px-4 py-2 rounded-full flex items-center dark:text-white'>
            {
              !currentAuthentication?.audience_data_updated_at ?
              'Has never been updated' :
              (() => {
                const date = new Date(currentAuthentication.audience_data_updated_at)
                if (!isValid(date)) return 'Has never been updated'
                return (
                  <span className='flex items-center'>
                    <Calendar className='mr-2 dark:text-rb-dark-mode-text-grey' />
                    <span className='text-rb-lighter-grey dark:text-gray-400 text-sm'>Last update:</span>
                    <span className='ml-1 text-sm dark:text-white'>{format(date, 'yyyy.MM.dd')}</span>
                  </span>
                )
              })()
            }
          </p>
          {
            !isAdvertiserContext && !isInfluencerShareContext && currentAuthentication?.provider && !currentAuthentication?.manually_entered && ['instagram', 'tiktok'].includes(currentAuthentication.provider) && (
              <div>
              {!currentAuthentication.audience_data_updated_at || !isValid(new Date(currentAuthentication.audience_data_updated_at)) || isBefore(new Date(currentAuthentication.audience_data_updated_at), subWeeks(new Date(), 1)) ? <button className="flex items-center bg-rb-green text-white p-2 px-3 rounded-md dark:text-rb-dark-mode" onClick={requestInfluencerUpdate}>Update data</button> : <p>You can update once in every week</p>}
              </div>
            )
          }
          {
            !isAdvertiserContext && !isInfluencerShareContext && currentAuthentication?.provider && currentAuthentication?.manually_entered && (
              <button
                className="flex items-center bg-rb-green text-white p-2 px-3 rounded-md dark:text-rb-dark-mode"
                onClick={() => influencerContext?.openAddAuthenticationModal?.('manual', currentAuthentication.provider)}
              >
                Edit account
              </button>
            )
          }
        </div>
        {!isAdvertiserContext && !isInfluencerShareContext && (
          <div className="flex flex-col gap-1 bg-rb-grey dark:bg-rb-dark-mode-lighter px-3 py-2 rounded-lg mt-4 self-end">
            {ratingCount > 0 ? (
              <>
                <RatingStars label="Professional" value={influencerContext?.professionalRating} />
                <RatingStars label="Quality" value={influencerContext?.qualityRating} />
              </>
            ) : (
              <span className="text-xs text-rb-mat-grey dark:text-gray-400">Not rated yet</span>
            )}
          </div>
        )}
        {isAdvertiserContext && campaign_authentication_id && (
          <div className="flex items-center gap-2 mt-4">
            <p className="text-sm dark:text-white">Selection status:</p>
            {advertiserCampaignAuth ? (
              <SelectionStatusPicker
                patchUrl={`/api/advertiser/campaign_share/campaign_authentications/${campaign_authentication_id}`}
                campaignAuthenticationId={campaign_authentication_id}
                selectionStatus={currentAuthentication?.selection_status ?? advertiserCampaignAuth?.selection_status ?? 'pending'}
                onSuccess={(value) => {
                  advertiserCampaignAuth.selection_status = value
                  if (currentAuthentication) currentAuthentication.selection_status = value
                }}
              />
            ) : (
              <SelectionStatusIcon
                selectionStatus={currentAuthentication?.selection_status ?? 'pending'}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
})
